// Villanova University: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Villanova's Common Data Set 2025-2026 (CDS-2025-2026-v3.pdf, hosted on
//    villanova.edu under the Office of the Provost's decision support
//    files). No CDS index page was found on villanova.edu (the Office of
//    Decision Support, Data and Insights' statistics pages link other
//    reports), so the source is the PDF itself; no 2026-2027 file or later
//    version of the 2025-2026 file is posted. C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21 early decision counts (Early Decision I and II
//    together, Fall 2025), C22 early action dates, G1/G5 costs (the file
//    gives 2026-2027 figures). The G1 tuition ($72,990) and the
//    Undergraduate Student Services Fee ($1,024) match the Bursar's
//    2026-2027 Tuition and Fees page, which also lists a $300 orientation
//    fee charged to first-year students (not in the CDS budget, not added
//    here).
//  - Villanova Undergraduate Admission: First-Year Applicants (application,
//    test policy, academic requirements by college), Application Dates and
//    Deadlines, and FAQs (test-optional policy). Departure: the admissions
//    page's decision dates differ from the CDS C21 dates (Early Decision I
//    by December 1 on the page, December 15 in the CDS; Early Decision II
//    by February 15 on the page, March 1 in the CDS); the admissions page
//    wins. The pages state the test-optional policy runs through the
//    2026-27 admission campaign (Class of 2031, fall 2027 entry), and that
//    Villanova continues "ongoing conversations" about testing beyond that
//    time; no fall 2028 decision is announced.
//  - College Scorecard, school id 216597 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Villanova Common Data Set 2025-2026',
        url: 'https://www.villanova.edu/content/dam/villanova/provost/decision_support/CDS-2025-2026-v3.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Villanova Undergraduate Admission, First-Year Applicants',
        url: 'https://www.villanova.edu/university/undergraduate-admission/applying-to-villanova/first-year-applicants.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Villanova Undergraduate Admission, Application Dates and Deadlines',
        url: 'https://www.villanova.edu/university/undergraduate-admission/applying-to-villanova/dates-and-deadlines.html',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Villanova Undergraduate Admission, FAQs',
        url: 'https://www.villanova.edu/university/undergraduate-admission/faqs.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?216597-Villanova-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'villanova-university',
    kbId: 'villanova_university',
    name: 'Villanova University',
    shortName: 'Villanova',
    location: 'Villanova, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Villanova visual identity, university
    // colors); decoration only.
    branding: {
        primary: '#002664',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/villanova_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 25841,
        admitted: 7272,
        enrolled: 1805,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 522, admitted: 216 },
            { group: 'Out-of-state', applied: 23777, admitted: 6882 },
            { group: 'International', applied: 1542, admitted: 174 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (by known physical address at the time of application)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Villanova\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision I and II, together', applied: 1680, admitted: 1005 },
        ],
        note: 'The Common Data Set counts both Early Decision rounds together. It gives the early action dates but no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Villanova reads every application as a whole. Three colleges ask for specific high school courses:',
        rows: [
            { school: 'Villanova School of Business', requirement: 'Four years of high school math, including Calculus if your school offers it. Most successful applicants have taken at least one year of Calculus beyond Pre-Calculus by the end of 12th grade.' },
            { school: 'College of Engineering', requirement: 'A Physics course and Pre-Calculus are required; Calculus is strongly encouraged if your school offers it.' },
            { school: 'Fitzpatrick College of Nursing', requirement: 'At least one year of Biology and one of Chemistry; advanced levels of both are strongly encouraged.' },
        ],
        note: 'Villanova does not conduct admission interviews and does not consider demonstrated interest.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 20', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'By December 1', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'By April 1', binding: false },
        ],
        note: 'Early Action and Early Decision I applicants hear admit, deny or defer; Early Decision II and Regular Decision applicants hear admit, deny or waitlist. You can switch from Early Action to Early Decision I until November 10, and from Regular Decision or a deferred Early Action application to Early Decision II until January 31.',
        source: 'deadlines',
        binding: 'Both Early Decision rounds are binding: if admitted, you enroll at Villanova and withdraw all applications to other colleges and universities. Early Decision admits pay the enrollment deposit within two weeks of admission.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Villanova is test-optional through the 2026-27 admission campaign (the Class of 2031, entering fall 2027): you choose whether to send SAT or ACT scores. You can self-report them on your Applicant Status Page and send official scores only if admitted and enrolling. Villanova superscores. Students homeschooled for more than one full academic year of high school must submit the SAT or ACT.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants and explains that they are required for homeschool students.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    // Villanova's FAQs say it continues "ongoing conversations about the
    // future of standardized testing" beyond the Class of 2031; no decision
    // is announced.
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'faqs',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72990 },
            { item: 'Required fees', amount: 1024 },
            { item: 'Housing (on campus)', amount: 10314 },
            { item: 'Food (on-campus meal plan)', amount: 9040 },
            { item: 'Books and supplies', amount: 1160 },
            { item: 'Transportation', amount: 1070 },
            { item: 'Other expenses', amount: 1450 },
        ],
        total: 97048,
        note: 'For a full-time first-year student living on campus, the full academic year, as the Common Data Set gives it. Villanova reports that tuition and fees do not vary by year of study or by program.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 41536,
            byIncome: [
                { income: '$0 to $30,000', amount: 21459 },
                { income: '$30,001 to $48,000', amount: 15356 },
                { income: '$48,001 to $75,000', amount: 25787 },
                { income: '$75,001 to $110,000', amount: 31719 },
                { income: 'Over $110,000', amount: 57135 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Villanova\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
